'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { TimeRangeConfig } from '@/api/generated/model';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { SPEND_GROUP_OTHER_LABEL, type SpendGroupKind } from '@/domains/pages/ai-spend/constants';
import { buildAiSpendGroupAggregatesPayload } from '@/domains/pages/ai-spend/utils/ai-spend-api-payloads';
import type { CompositionSegment } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import {
  buildSpendCompositionSegments,
  mapGroupSpendRows,
  type GroupSpendRow,
} from '@/domains/pages/ai-spend/utils/map-ai-spend-distribution';
import { NEBULY_PAGE_QUERY_OPTIONS, getPageTimeRangeQueryKey } from '@/domains/pages/shared/page-query-options';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import { useNebulyPageEffectiveNow } from '@/domains/pages/hooks/use-nebuly-page-effective-now';
import { useNebulyPageStableNow } from '@/domains/pages/hooks/use-nebuly-page-stable-now';
import { getTimeRangeMemoKey } from '@/domains/pages/utils/nebuly-page-time-range';
import { useCallback, useMemo } from 'react';

export type AiSpendDistributionData = {
  rows: GroupSpendRow[];
  compositionSegments: CompositionSegment[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

type UseAiSpendDistributionDataParams = {
  projectId: string | undefined;
  timeRange: TimeRangeConfig | undefined;
  filters?: GenericFilter[];
  specialFilters?: GenericFilter[];
  kind: SpendGroupKind;
  totalCost: number | null;
  enabled?: boolean;
};

export function useAiSpendDistributionData({
  projectId,
  timeRange,
  filters = [],
  specialFilters = [],
  kind,
  totalCost,
  enabled = true,
}: UseAiSpendDistributionDataParams): AiSpendDistributionData {
  const stableNow = useNebulyPageStableNow();
  const { effectiveNow, isReady: isEffectiveNowReady } = useNebulyPageEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const timeRangeMemoKey = getTimeRangeMemoKey(timeRange);

  const resolvedTimeRange = useMemo(
    () => resolveNebulyPageTimeRange(timeRange, stableNow, effectiveNow),
    [timeRangeMemoKey, stableNowKey, effectiveNowKey],
  );

  const timeRangeQueryKey = useMemo(() => getPageTimeRangeQueryKey(resolvedTimeRange), [resolvedTimeRange]);

  const filtersQueryKey = useMemo(() => JSON.stringify(filters), [filters]);
  const specialFiltersQueryKey = useMemo(() => JSON.stringify(specialFilters), [specialFilters]);
  const queryEnabled =
    enabled && Boolean(projectId && resolvedTimeRange && totalCost != null && isEffectiveNowReady);

  const distributionQuery = useQuery({
    queryKey: [
      QUERY_KEY_INTERACTION_AGGREGATES,
      'ai-spend-distribution',
      kind,
      projectId,
      timeRangeQueryKey,
      filtersQueryKey,
      specialFiltersQueryKey,
    ],
    queryFn: ({ signal }) =>
      getInteractionAggregates(
        buildAiSpendGroupAggregatesPayload({
          projectId: projectId!,
          timeRange: resolvedTimeRange!,
          filters,
          specialFilters,
          kind,
        }),
        { signal },
      ),
    enabled: queryEnabled,
    ...NEBULY_PAGE_QUERY_OPTIONS,
  });

  const rows = useMemo(() => {
    if (!distributionQuery.data || totalCost == null) return [];
    return mapGroupSpendRows(distributionQuery.data, totalCost, kind);
  }, [distributionQuery.data, kind, totalCost]);

  const compositionSegments = useMemo(
    () =>
      buildSpendCompositionSegments(rows, {
        otherLabel: SPEND_GROUP_OTHER_LABEL[kind],
        kind,
        totalCost,
      }),
    [rows, kind, totalCost],
  );

  const refetch = useCallback(() => {
    void distributionQuery.refetch();
  }, [distributionQuery.refetch]);

  return {
    rows,
    compositionSegments,
    isLoading: queryEnabled && distributionQuery.isLoading,
    isError: distributionQuery.isError,
    error: distributionQuery.error,
    refetch,
  };
}
